// Geteilte Zeilen-Komponente fuer Kaufland-Feld-Zuordnungen.
//
// Aufrufer ist der Mapping-Editor (views/tabs/kaufland-list-mapping.ts). Bis 2026-08-06 gab es
// einen zweiten, den Reiter "Optionale Felder" (views/tabs/optional-fields.ts) — der zeigte nach
// dem Umbau auf das Mapping-Modell dieselben Zeilen wie die Gruppe "Optionale Felder" im Editor
// und ist deshalb entfernt. Das Modul bleibt getrennt: es haelt die Zeilen-Darstellung samt ihrer
// CSS zusammen und ist die Stelle, an der ein kuenftiger zweiter Aufrufer ansetzt, ohne zu kopieren.
//
// Hier steht ausschliesslich das Rendern und die Klassifikation. Kein Event-Wiring, kein State:
// die Ansicht bindet ihre Events selbst. Die HTML-Struktur samt kl-map-*-Klassen und
// data-field-*-Attributen ist Vertrag mit dem Aufrufer und mit packages/frontend-design/styles.

import { renderCombobox } from './combobox.js';

export interface SchemaField {
  key: string;
  apiTarget: string;
  label: string;
  description?: string | null;
  dataType: string;
  required: boolean;
  requiredCondition?: string;
  allowedValues?: Array<{ value: string; label?: string | null; rate?: number | null }>;
  /** Feldregeln, ausgeliefert von `kaufland-listing-schema/feldregeln.ts` (2026-08-24).
   *  Sie werden NICHT hier entschieden -- MCP und Lister lesen dieselbe Antwort, sonst
   *  gaebe es zwei Wahrheiten darueber, was belegbar ist. Fehlt ein Flag (aeltere
   *  Function-Version), gilt "erlaubt": ein noch nicht deploytes Schema darf die Maske
   *  nicht leerraeumen. */
  haendlerpflege?: boolean;
  festwertKategorie?: boolean;
  festwertListing?: boolean;
  /** Warum eingeschraenkt -- Text aus der Regeltabelle, fuer den Haendler gedacht. */
  grund?: string;
  /** true, wenn Kaufland fuer dieses Attribut vordefinierte Werte hat, der Cache dafuer aber
   *  noch leer ist, weil der Refresh-Job (refresh_shared_set_values) noch laeuft. */
  sharedSetPending?: boolean;
  /** true, wenn Kauflands Werteliste laenger als das Auslieferungslimit (500) war und deshalb
   *  gekuerzt wurde. Die Combobox muss dann weiterhin freie Eingabe erlauben. */
  allowedValuesTruncated?: boolean;
  /** Herkunft von `allowedValues` (Fix-Runde 1, 2026-08-09).
   *  `'enum'`: geschlossene, vollstaendige Codeliste (z.B. condition, vat_indicator,
   *  id_shipping_group) -- ein Wert ausserhalb ist immer ein Fehler, deshalb bleibt es ein
   *  `<select>` ohne Freitext.
   *  `'shared_set'`: Kauflands attributweite Werteliste, kann laut `allowedValuesTruncated`
   *  unvollstaendig sein -- dort ist Handeingabe per Combobox noetig.
   *  Fehlt das Feld, gilt derselbe strenge Pfad wie bei `'enum'` (sicherer Default). */
  allowedValuesSource?: 'enum' | 'shared_set';
  sharedSetId?: number;
  maxLength?: number;
  isMultilingual?: boolean;
  isVariantRelevant?: boolean;
  defaultSourceKey?: string;
  unit?: string | null;
  fieldGroup: string;
}

export interface SourceDef {
  sourceKey: string;
  path: string;
  dataType: string;
  label: string;
  /** Bei action=list mit listingId/articleId: konkreter Beispielwert aus Vario. */
  exampleValue?: string | number | null;
  isVariantAttribute?: boolean;
  /** Bei article_images: die Vario-DMS-Resource-ID, die hinter dieser Source steckt. */
  varioResourceId?: string;
  /** Bei article_images: ggf. schon hochgeladene Public-URL (preview-tauglich). */
  publicUrl?: string;
}

/** Eine Hersteller-Bezeichnung aus Varios `account.manufacturer.labels[]`, geliefert von
 *  `vario-mapping-sources` fuer den Hersteller des angefragten Artikels. */
export interface ManufacturerLabelOption {
  id: string | null;
  label: string;
  /** ISO 639-1, lowercase; null wenn Vario keine Sprache liefert. */
  languageCode: string | null;
}

export interface SourcesResp {
  groups: Array<{ key: string; label: string; sources: SourceDef[] }>;
  /** Bezeichnungen des Hersteller-Kontos -- Auswahl fuer den Artikelwert am Feld
   *  `manufacturer` (renderOverrideInput). Fehlt bei aelteren Antworten und ohne Hersteller. */
  manufacturerLabels?: ManufacturerLabelOption[];
  meta?: {
    varioBaseUrl?: string;
    [key: string]: unknown;
  };
}

export interface ExistingFieldMapping {
  kaufland_field_key: string;
  field_group: string;
  /** `clear` ist keine Quelle, sondern ein Auftrag: Attribut bei Kaufland entfernen
   *  (leeres Array im Produktdaten-PATCH, gemessen 2026-08-20). Siehe CLEAR_VALUE. */
  source_type: 'vario_path' | 'fixed_value' | 'template' | 'unmapped' | 'vario_dms_resource' | 'clear';
  vario_path: string | null;
  fixed_value: unknown;
  template: string | null;
  fallback: unknown;
  vario_resource_id?: string | null;
  slot_index?: number | null;
}

export interface FieldAlias {
  key: string;
  fieldGroup: string;
  apiTarget: string;
}

/** Display-Field: nach `key` dedupliziertes SchemaField, mit Aliases fuer Save-Fanout.
 *  Wenn dasselbe key in mehreren API-Groups vorkommt (z.B. 'ean' in product_data_basic
 *  UND in offer), wird im UI nur ein Eintrag gezeigt; beim Save schreiben wir den gleichen
 *  Mapping fuer jeden Alias-Eintrag (eine Zeile pro field_group). */
export interface DisplayField extends SchemaField {
  aliases: FieldAlias[];
  originalFieldGroup: string;
}

/** Niedrigerer Wert = hoehere Priority. Bei Konflikt gewinnt das hoeher-priorisierte
 *  fieldGroup als "anzeige-group" + Label/Description-Quelle. */
export const GROUP_PRIORITY: Record<string, number> = {
  product_data_basic: 1,
  // images: entfernt 2026-05-24 — Bilder gehen ueber den Lister-Tab "Bilder", nicht
  // mehr ueber picture_<n>-Mappings.
  category_required_attributes: 3,
  category_optional_attributes: 4,
  gpsr: 5,
  compliance: 6,
  variants: 7,
  offer: 8,
};

export const GROUP_LABELS: Record<string, string> = {
  product_data_basic: 'Basisdaten',
  category_required_attributes: 'Kategorie',
  category_optional_attributes: 'Kategorie (opt)',
  gpsr: 'GPSR',
  compliance: 'Compliance',
  variants: 'Varianten',
  offer: 'Angebot',
};

/**
 * Klassifiziert Felder, die typischerweise in Vario am Artikel gepflegt werden, fuer die
 * neue UI-Gruppe "Vario Felder". Match nach key (deterministisch fuer top-level schema
 * fields) und nach label-pattern (fuer Kaufland-Kategorie-Attribute, deren key je
 * Kategorie variiert und nur als deutscher Label-Text stabil bleibt). Reihenfolge:
 *   - mpn, list_price, id_shipping_group, product_safety_contact.* (top-level)
 *   - Grundpreiseinheit/-menge, Maße (Länge/Breite/Höhe/Tiefe), Gewicht, Stückzahl,
 *     Online-Bestellnummer (per label, da als category_attribute reinkommend)
 */
// Optionale Kaufland-Attribute (per stabilem Namen = field.key), die einem Vario-Feld am
// Artikel entsprechen. Entscheidet NUR die Anzeigegruppe: "Vario Felder" statt "Optionale
// Felder". Seit 2026-08-06 entscheidet die Menge nicht mehr, ob ein Feld ueberhaupt im Editor
// erscheint, siehe splitCategoryAttributes in kaufland-listing-schema.
// Spiegel: SQL kaufland.optional_attr_is_vario_mapped (Migration 0124/0128).
const VARIO_OPTIONAL_ATTR_KEYS = new Set<string>([
  'mpn', 'real_mgb_artnum', 'quantity', 'weight', 'base_price_unit', 'content_volume',
  'product_dimensions', 'width', 'height', 'depth', 'diameter',
]);

// Felder, die keine Kategorie-Zuordnung bekommen, weil sie ausschliesslich als Direktwert
// gepflegt werden. Sie fallen aus `displayFields` des Editors heraus und werden dort separat
// gerendert (renderMinPriceRow in kaufland-list-mapping.ts).
//
// Das ist nur der Mindestpreis: der Preis-Push liest ihn per SQL aus
// kaufland.optional_field_values (apps/worker/src/jobs/kaufland-push-prices.ts), er koennte
// eine Vario-Quelle nicht aufloesen. Kurzbeschreibung und Kunden-Hinweis sind seit 2026-08-06
// normal zuordbar.
const DIRECT_VALUE_ONLY_FIELD_KEYS = new Set<string>(['minimum_price']);

export function isDirectValueOnlyField(key: string): boolean {
  return DIRECT_VALUE_ONLY_FIELD_KEYS.has(key);
}

/**
 * Darf der Haendler dieses Feld ueberhaupt pflegen?
 *
 * BIS ZUM 2026-08-24 stand dahinter ein handgepflegtes Set im Frontend
 * (`NOT_ACCEPTED_BY_KAUFLAND_KEYS` mit title_ai, real_mgb_artnum, rich_content). Es hatte
 * einen Fehler, den man ihm nicht ansah: Nur der Lister kannte es. Der MCP konnte
 * dieselben Felder ungehindert belegen. Die Regel kommt jetzt mit der Schema-Antwort und
 * gilt fuer beide Verbraucher.
 *
 * Ein fehlendes Flag heisst "erlaubt" -- siehe Kommentar am Typ.
 */
export function darfGepflegtWerden(f: { haendlerpflege?: boolean }): boolean {
  return f.haendlerpflege !== false;
}

/** Darf ein fester Wert fuer die ganze Kategorie gelten? */
export function darfKategorieFestwert(f: { festwertKategorie?: boolean }): boolean {
  return f.festwertKategorie !== false;
}

/** Darf ein fester Wert nur fuer dieses Listing gelten? */
export function darfListingFestwert(f: { festwertListing?: boolean }): boolean {
  return f.festwertListing !== false;
}

export function isVarioField(f: SchemaField): boolean {
  if (VARIO_OPTIONAL_ATTR_KEYS.has(f.key)) return true;
  if (f.key === 'mpn' || f.key === 'id_shipping_group' || f.key === 'list_price') return true;
  // UVP (list_price) -> Vario-Feld. Mindestpreis (minimum_price) ist der einzige Direktwert,
  // er wird ueber isDirectValueOnlyField aus dem Editor gefiltert. Kurzbeschreibung und
  // Kunden-Hinweis sind gewoehnliche optionale Felder.
  // Parent-EAN und Varianten-Attribute standen hier bis zum 2026-08-16. Sie sind aus dem
  // Schema entfernt, weil sie am Variantenvater geregelt werden — der Sonderfall hier
  // liefe seither ins Leere.
  if (f.key.startsWith('product_safety_contact.')) return true;
  const lbl = f.label.toLowerCase();
  if (lbl.includes('grundpreis')) return true;
  if (lbl.includes('maße') || lbl.includes('länge') || lbl.includes('breite') || lbl.includes('höhe') || lbl.includes('tiefe')) return true;
  if (lbl.includes('gewicht')) return true;
  if (lbl.includes('stückzahl')) return true;
  if (lbl.includes('hersteller-artikelnummer')) return true;
  if (lbl.includes('bestellnummer')) return true;
  return false;
}

/** Uebernimmt die Auswahlwert-Felder aus dem unterlegenen ins siegreiche Feld, wenn das
 *  siegreiche keine hat.
 *
 *  Abschlussreview 2026-08-09, Befund K2: `manufacturer` kommt zweimal an. Einmal als
 *  statisches Basisfeld (Gruppe `product_data_basic`, Prioritaet 1, OHNE allowedValues) und
 *  einmal als Kaufland-Attribut 21 (Gruppe `category_required_attributes`, Prioritaet 3, MIT
 *  allowedValues aus dem Shared Set). Die reine Prioritaetsentscheidung liess das Basisfeld
 *  gewinnen und warf Kauflands Markenliste weg: Der Cache war gefuellt, die Edge Function
 *  lieferte die Werte korrekt, und der Editor zeigte fuer den Hersteller trotzdem ein freies
 *  Textfeld. Attribut 21 ist in 20 der bisher genutzten Kategorien Pflicht.
 *
 *  Bewusst feldweise statt ueber eine geaenderte Gruppenreihenfolge: Label, Beschreibung und
 *  Anzeigegruppe sollen weiter vom hoeher priorisierten Feld kommen, und der Fix traegt auch
 *  fuer kuenftige Namensvettern, deren Gruppen wir heute nicht kennen. */
function uebernimmAuswahlwerte(sieger: DisplayField, unterlegen: SchemaField): void {
  if (sieger.allowedValues === undefined && unterlegen.allowedValues !== undefined) {
    sieger.allowedValues = unterlegen.allowedValues;
  }
  if (sieger.allowedValuesSource === undefined && unterlegen.allowedValuesSource !== undefined) {
    sieger.allowedValuesSource = unterlegen.allowedValuesSource;
  }
  if (sieger.sharedSetPending === undefined && unterlegen.sharedSetPending !== undefined) {
    sieger.sharedSetPending = unterlegen.sharedSetPending;
  }
  if (sieger.allowedValuesTruncated === undefined && unterlegen.allowedValuesTruncated !== undefined) {
    sieger.allowedValuesTruncated = unterlegen.allowedValuesTruncated;
  }
}

export function dedupFieldsByKey(fields: SchemaField[]): DisplayField[] {
  const byKey = new Map<string, DisplayField>();
  for (const f of fields) {
    const existing = byKey.get(f.key);
    const alias: FieldAlias = { key: f.key, fieldGroup: f.fieldGroup, apiTarget: f.apiTarget };
    if (!existing) {
      byKey.set(f.key, { ...f, aliases: [alias], originalFieldGroup: f.fieldGroup });
      continue;
    }
    existing.aliases.push(alias);
    const eP = GROUP_PRIORITY[existing.originalFieldGroup] ?? 99;
    const fP = GROUP_PRIORITY[f.fieldGroup] ?? 99;
    if (fP < eP) {
      // f hat hoehere prio — uebernimm dessen meta (label, description, allowedValues, etc.)
      // aber merge die aliasliste
      const neu: DisplayField = { ...f, aliases: existing.aliases, originalFieldGroup: f.fieldGroup };
      // ... ausser den Auswahlwerten: die duerfen nicht verloren gehen, siehe Befund K2.
      uebernimmAuswahlwerte(neu, existing);
      byKey.set(f.key, neu);
    } else {
      uebernimmAuswahlwerte(existing, f);
    }
    const merged = byKey.get(f.key)!;
    merged.required = merged.required || f.required;
  }
  return [...byKey.values()];
}

// ---------- Edge-Function-Aufruf ----------

export interface EdgeFnCtx {
  supabaseUrl: string;
  session: { token: string };
}

export async function callFn<T>(
  ctx: EdgeFnCtx,
  name: string,
  body: unknown,
): Promise<{ ok: true; value: T } | { ok: false; error: string }> {
  try {
    const res = await fetch(`${ctx.supabaseUrl.replace(/\/$/, '')}/functions/v1/${name}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${ctx.session.token}`,
      },
      body: JSON.stringify(body),
    });
    const text = await res.text();
    let parsed: unknown;
    try { parsed = text ? JSON.parse(text) : {}; } catch { parsed = { raw: text }; }
    if (!res.ok) {
      const errBag = (parsed as { error?: { code?: string; message?: string } } | null)?.error;
      return { ok: false, error: errBag ? `${errBag.code ?? '?'}: ${errBag.message ?? '?'}` : `HTTP ${res.status}` };
    }
    return { ok: true, value: parsed as T };
  } catch (e) {
    return { ok: false, error: (e as Error).message };
  }
}

// ---------- Row rendering ----------

export function renderFieldRow(
  f: DisplayField,
  row: { sources: SourcesResp; mapping: ExistingFieldMapping | undefined; override: unknown },
  opts: { withCreateEav?: boolean } = {},
): string {
  const m = row.mapping;
  const status = fieldStatus(f, m);
  // Beschreibung nur zeigen, wenn sie sich vom Label unterscheidet — sonst doppelt
  // (viele Kaufland-Attribute haben description == label, z.B. "Online Bestellnummer").
  const hasDistinctDesc = !!f.description && f.description.trim().toLowerCase() !== f.label.trim().toLowerCase();
  const description = hasDistinctDesc ? `<div class="kl-map-field-desc">${escapeHtml(f.description!)}</div>` : '';
  const condition = f.requiredCondition ? `<div class="kl-map-field-conditional">${escapeHtml(f.requiredCondition)}</div>` : '';
  const allowed = f.allowedValues && f.allowedValues.length > 0 ? renderAllowedValues(f) : '';
  const typeline = [
    f.dataType,
    f.maxLength ? `max ${f.maxLength}` : null,
    f.isMultilingual ? 'multilang' : null,
  ].filter(Boolean).join(' · ');
  // Push-Sektionen als Info-Icon mit Tooltip (vorher 1-N Chips pro Section).
  const aliasLabels = f.aliases.map((a) => GROUP_LABELS[a.fieldGroup] ?? a.fieldGroup);
  const uniqueAliasLabels = [...new Set(aliasLabels)];
  const sourceTooltip = uniqueAliasLabels.length > 0
    ? `Quelle: ${uniqueAliasLabels.join(', ')} · Feld-Code: ${f.key}`
    : `Feld-Code: ${f.key}`;
  // Search-Filter: data-search-label fuer simple text-match
  const searchTerm = `${f.label} ${f.key}`.toLowerCase();
  const requiredFlag = f.required ? '1' : '0';

  return `
    <div class="kl-map-field"
         data-status="${status}"
         data-field-key="${escapeHtml(f.key)}"
         data-search-label="${escapeHtml(searchTerm)}"
         data-required="${requiredFlag}">
      <div class="kl-map-field-meta">
        <div class="kl-map-field-label-row">
          <span class="kl-map-field-label" title="Feld-Code: ${escapeHtml(f.key)}">${escapeHtml(f.label)}</span>
          ${f.required ? `<span class="kl-map-field-req" title="Pflichtfeld">*</span>` : ''}
          <span class="kl-map-field-info v-icon fa-light fa-circle-info" title="${escapeHtml(sourceTooltip)}"></span>
        </div>
        <div class="kl-map-field-typeline">${escapeHtml(typeline)}</div>
        ${description}
        ${condition}
        ${allowed}
      </div>
      <div class="kl-map-field-source">
        ${renderSourceDropdown(f, row.sources, quellenAuswahl(m, row.override), {
          ...opts,
          withClear: darfGeloeschtWerden(f),
        })}
      </div>
      <div class="kl-map-field-value-shell">
        ${darfKategorieFestwert(f) ? renderValueInput(f, anzeigeMapping(m, row.override)) : ''}
        ${darfListingFestwert(f) ? renderOverrideInput(f, row.override, row.sources) : ''}
      </div>
    </div>
  `;
}

/** Listing-spezifischer Override (Migration 0129): ein fester Wert nur fuer dieses Listing,
 *  der die Profil-Quelle uebersticht. Leer = erbt Profil. Bei Enum-Feldern ein Select mit
 *  den erlaubten Werten, sonst Text/Zahl je nach dataType. Gesetzte Overrides werden
 *  hervorgehoben (orangener Rahmen), damit klar ist, dass dieses Feld vom Profil abweicht. */
export function renderOverrideInput(f: DisplayField, ov: unknown, sources?: SourcesResp): string {
  const isSet = ov !== undefined && ov !== null && ov !== '';
  const curText = ov === undefined || ov === null
    ? ''
    : (typeof ov === 'object' ? JSON.stringify(ov) : String(ov));
  const cls = `kl-map-field-override-input${isSet ? ' is-set' : ''}`;
  let input: string;
  const bezeichnungen = f.key === 'manufacturer' ? (sources?.manufacturerLabels ?? []) : [];
  if (bezeichnungen.length > 0) {
    // Hersteller: Vario fuehrt am Hersteller-Konto mehrere Bezeichnungen (je Sprache eine oder
    // mehrere). Die Profil-Quelle `manufacturer.label` nimmt die in der Storefront-Sprache,
    // sonst die erste; welche der uebrigen gelten soll, entscheidet der Haendler HIER, pro
    // Artikel. Datalist statt Select, weil ein von Hand getippter Markenname weiter erlaubt
    // bleibt -- Kauflands Markenliste ist mit 18.000 Werten bewusst nicht angebunden.
    input = renderCombobox({
      fieldKey: f.key,
      werte: bezeichnungen.map((b) => ({
        value: b.label,
        label: b.languageCode ? `${b.label} (${b.languageCode})` : b.label,
      })),
      aktuell: curText,
      platzhalter: 'Bezeichnung wählen oder eingeben (erbt sonst Profil)',
      datenAttribut: 'data-field-override',
      klasse: cls,
      listenSuffix: 'override',
    });
  } else if (f.allowedValues && f.allowedValues.length > 0) {
    const opts = [`<option value="">— erbt Profil —</option>`]
      .concat(f.allowedValues.map((av) =>
        `<option value="${escapeHtml(av.value)}"${av.value === curText ? ' selected' : ''}>${escapeHtml(av.label ?? av.value)}</option>`));
    input = `<select class="${cls}" data-field-override="${escapeHtml(f.key)}">${opts.join('')}</select>`;
  } else {
    const type = (f.dataType === 'currency_cents' || f.dataType === 'integer' || f.dataType === 'float') ? 'number' : 'text';
    input = `<input type="${type}" class="${cls}" data-field-override="${escapeHtml(f.key)}" value="${escapeHtml(curText)}" placeholder="eigener Wert (erbt sonst Profil)" />`;
  }
  return `
    <div class="kl-map-field-override"
         title="Eigener Wert nur für diesen Artikel - sticht die Profil-Quelle. Leer = erbt das Profil-Mapping.">
      ${input}
      <div class="kl-map-field-override-cap${isSet ? ' is-set' : ''}">${isSet ? '✓ nur dieser Artikel' : 'nur dieser Artikel'}</div>
    </div>`;
}

export function sourceSelectValue(m: ExistingFieldMapping): string {
  if (m.source_type === 'clear') return CLEAR_VALUE;
  if (m.source_type === 'fixed_value') return '__fixed_value__';
  if (m.source_type === 'template') return '__template__';
  if (m.source_type === 'vario_path' && m.vario_path) return m.vario_path;
  if (m.source_type === 'vario_dms_resource' && m.vario_resource_id) {
    return `article.dms.image_${m.vario_resource_id}`;
  }
  return '__unmapped__';
}

/** Sentinel-Wert der letzten Dropdown-Zeile: öffnet den Anlege-Dialog statt zu mappen. */
export const CREATE_EAV_VALUE = '__create_eav__';

/**
 * Sentinel für „Wert bei Kaufland löschen" (`source_type='clear'`).
 *
 * Anlass (2026-08-20): Ein Attribut mit falschem Wert liess sich nicht mehr loswerden. Das
 * Feld zu leeren half nicht — der Payload-Bauer verwirft leere Werte, und
 * `PUT /v2/product-data/` ergaenzt, statt zu ersetzen. An der Live-API gemessen: ein leeres
 * Array (`attributes: { key: [] }`) entfernt das Attribut.
 *
 * Es ist bewusst eine EIGENE Auswahl und nicht „leerer Festwert". In der Datenbank lagen 22
 * Zeilen mit leerem `fixed_value` — Ueberbleibsel der alten Vorbelegung, darunter `title` und
 * `description`. Waere „leer" als Loeschauftrag ausgelegt worden, haette der naechste Push
 * sie bei Kaufland entfernt.
 */
export const CLEAR_VALUE = '__clear__';

/**
 * Feldgruppen, in denen ein leeres Array technisch nichts ausrichtet.
 *
 * `offer` geht per `PATCH /v2/units` an das Angebot, und `UnitPatchRequest` kennt kein
 * Loeschen. `gpsr`/`compliance` sind ein OBJEKT (`product_safety_contact`), kein Array.
 * `images` kommt aus der Bilder-Freigabe, nicht aus dem Mapping. `variants` ist nicht
 * abgedeckt. Ueberall dort waere die Auswahl ein leeres Versprechen.
 */
const OHNE_LOESCHWEG = new Set(['offer', 'gpsr', 'compliance', 'images', 'variants']);

/**
 * Darf dieses Feld bei Kaufland geloescht werden?
 *
 * Massgeblich ist `required`, NICHT die Feldgruppe. Die erste Umsetzung grenzte nach Gruppe
 * ab (nur `category_optional_attributes`) und schloss damit optionale Basisfelder wie
 * `short_description` und `mpn` faelschlich aus. Vorgabe des Betreibers am 2026-08-20: „Wir
 * hatten doch gesagt alle optionalen Felder."
 *
 * Pflichtfelder bleiben aussen vor: Kaufland verlangt sie, ein leeres Array duerfte die
 * naechste Pruefung kippen -- und `title`, `description`, `manufacturer` tragen `required:
 * true` im Schema (nachgesehen, nicht angenommen). Die EAN ist die Produktidentitaet, kein
 * Wert daran.
 *
 * Der Payload-Bauer prueft eine zweite, gruppenbasierte Bedingung -- er kennt `required`
 * nicht. Zwei Schichten, weil ein einmal gespeichertes `clear` sonst dauerhaft Schaden
 * anrichten koennte.
 */
export function darfGeloeschtWerden(f: DisplayField): boolean {
  if (f.required) return false;
  if (f.key === 'ean') return false;
  return !OHNE_LOESCHWEG.has(f.originalFieldGroup);
}

/** Praefix der Profil-Quellen je Hersteller-Bezeichnung, `manufacturer.label#<Vario-ID>`. Spiegel
 *  von MANUFACTURER_LABEL_SOURCE_PREFIX in packages/kaufland-product-data/src/index.ts; das
 *  Frontend importiert das Paket nicht. */
export const MANUFACTURER_LABEL_SOURCE_PREFIX = 'manufacturer.label#';

/**
 * Ersatzzeile fuer eine gespeicherte Bezeichnungs-Quelle, die in der Liste fehlt.
 *
 * Die Zeilen je Bezeichnung baut `vario-mapping-sources` aus dem Hersteller-Konto des gerade
 * geoeffneten Artikels. Das Profil gilt aber je Kategorie: Bei einem Artikel eines ANDEREN
 * Herstellers steht der gespeicherte Schluessel nicht in der Liste, und ein <select> ohne
 * passende Option zeigt still seine erste, also „nicht gemappt", obwohl der Push die Regel von
 * `manufacturer.label` anwendet. Deshalb bekommt der Schluessel eine eigene, benannte Zeile.
 * null, wenn keine Bezeichnungs-Quelle gewaehlt ist oder die Liste sie kennt.
 */
export function fehlendeBezeichnungsQuelle(sources: SourcesResp, selectedValue: string): SourceDef | null {
  if (!selectedValue.startsWith(MANUFACTURER_LABEL_SOURCE_PREFIX)) return null;
  const bekannt = sources.groups.some((g) => g.sources.some((s) => s.sourceKey === selectedValue));
  if (bekannt) return null;
  const id = selectedValue.slice(MANUFACTURER_LABEL_SOURCE_PREFIX.length).trim();
  // Der Push sendet in diesem Fall den Wert der generischen Quelle; ihr Beispielwert zeigt dem
  // Haendler, was tatsaechlich rausgeht (oder „Hersteller am Artikel fehlt …").
  const generisch = sources.groups.flatMap((g) => g.sources).find((s) => s.sourceKey === 'manufacturer.label');
  const beispiel = generisch?.exampleValue;
  return {
    sourceKey: selectedValue,
    path: `manufacturer.labels[id=${id}].label`,
    dataType: 'string',
    label: `Hersteller-Bezeichnung (ID ${id}, an diesem Hersteller nicht vorhanden, Rückfall: Sprache der Storefront)`,
    ...(beispiel !== undefined && beispiel !== null ? { exampleValue: beispiel } : {}),
  };
}

export function renderSourceDropdown(
  f: SchemaField,
  sources: SourcesResp,
  selectedValue: string,
  opts: { withCreateEav?: boolean; withClear?: boolean } = {},
): string {
  const opts_: string[] = [`<option value="__unmapped__">— nicht gemappt —</option>`];
  const option = (s: SourceDef): string => {
    const selected = s.sourceKey === selectedValue ? ' selected' : '';
    const ex = s.exampleValue !== undefined && s.exampleValue !== null && String(s.exampleValue).length > 0
      ? ` - ${String(s.exampleValue)}`
      : '';
    return `<option value="${escapeHtml(s.sourceKey)}"${selected}>${escapeHtml(s.label)}${escapeHtml(ex)}</option>`;
  };
  const ersatz = fehlendeBezeichnungsQuelle(sources, selectedValue);
  let ersatzEingefuegt = false;
  for (const group of sources.groups) {
    const zeilen = ersatz && group.key === 'manufacturer' ? [...group.sources, ersatz] : group.sources;
    if (zeilen.length === 0) continue;
    if (zeilen !== group.sources) ersatzEingefuegt = true;
    opts_.push(`<optgroup label="${escapeHtml(group.label)}">`);
    for (const s of zeilen) opts_.push(option(s));
    opts_.push(`</optgroup>`);
  }
  if (ersatz && !ersatzEingefuegt) {
    // Antwort ohne Hersteller-Gruppe (aeltere Function, Fehlerfall): eigene Gruppe, damit die
    // gespeicherte Quelle trotzdem sichtbar und ausgewaehlt bleibt.
    opts_.push(`<optgroup label="Hersteller (vom Artikel)">${option(ersatz)}</optgroup>`);
  }
  if (opts.withClear) {
    // Abgesetzt, weil es keine Quelle ist, sondern ein Auftrag an Kaufland: das Attribut
    // dort entfernen. Nur bei optionalen Kategorie-Attributen, siehe darfGeloeschtWerden.
    const selected = selectedValue === CLEAR_VALUE ? ' selected' : '';
    opts_.push(`<optgroup label="Bei Kaufland">`);
    opts_.push(`<option value="${CLEAR_VALUE}"${selected}>🗑 Wert bei Kaufland löschen</option>`);
    opts_.push(`</optgroup>`);
  }
  if (opts.withCreateEav) {
    // Ganz unten, abgesetzt: Feld in Vario neu anlegen. Findet man fuer ein Kaufland-Feld
    // keine Quelle, ist der Direktwert die schlechtere Antwort. Nur der Mapping-Editor
    // bringt den Anlege-Dialog mit, deshalb der Schalter.
    opts_.push(`<optgroup label="Neu">`);
    opts_.push(`<option value="${CREATE_EAV_VALUE}">+ Zusatzfeld in Vario anlegen …</option>`);
    opts_.push(`</optgroup>`);
  }
  return `<select data-field-source="${escapeHtml(f.key)}">${opts_.join('')}</select>`;
}

/** Eingabeform des Direktwerts, abgeleitet aus dem Kaufland-Feldtyp.
 *
 *  `rows` steigt mit der erlaubten Länge: `description` (65535) bekommt ein deutlich größeres
 *  Feld als eine Kurzbeschreibung (1024). Ohne `maxLength` gilt der Typ allein. */
export function fixedValueLayout(f: SchemaField): { kind: 'longtext'; rows: number } | { kind: 'number' } | { kind: 'line' } {
  // Suchbegriffe zuerst, VOR langTyp und langGenug: short_description hat exakt
  // maxLength 1024 und faellt sonst ueber `langGenug` in den Textbereich, samt
  // HTML-Vorschau. Genau das darf dort nicht stehen.
  if (f.dataType === 'keywords') return { kind: 'line' };
  const langTyp = f.dataType === 'text' || f.dataType === 'small_text';
  const langGenug = (f.maxLength ?? 0) >= 1024;
  if (langTyp || langGenug) {
    const rows = (f.maxLength ?? 0) >= 20000 ? 12 : (f.maxLength ?? 0) >= 4096 ? 8 : 5;
    return { kind: 'longtext', rows };
  }
  if (f.dataType === 'integer' || f.dataType === 'float' || f.dataType === 'number' || f.dataType === 'currency_cents') {
    return { kind: 'number' };
  }
  return { kind: 'line' };
}

/** Ist ein Artikelwert gesetzt? Leerer Text, null und undefined zaehlen nicht. */
function hatArtikelwert(ov: unknown): boolean {
  return ov !== undefined && ov !== null && ov !== '';
}

/**
 * Was im Zuordnungs-Dropdown steht.
 *
 * VORGABE DES AUFTRAGGEBERS (2026-08-24): "Es muss fester Wert eingetragen sein, wo
 * derzeit nicht gemappt steht aber Werte vorhanden sind."
 *
 * Der Punkt dahinter: "Fester Wert" ist die ART der Quelle -- im Gegensatz zu einer
 * Vario-Quelle. WO er gilt, entscheidet, in welches der beiden Wertfelder man schreibt:
 * Kategorie oder nur dieser Artikel. Ein Feld, das seinen Wert aus dem Artikel bezieht,
 * ist deshalb nicht "nicht gemappt" -- es ist ein fester Wert mit Artikel-Geltung.
 *
 * WARUM NUR ANZEIGE, OHNE ZEILE: Eine Kategorie-Zeile `fixed_value` mit LEEREM Inhalt
 * waere ein Loeschauftrag an Kaufland (Migration 0054, index.ts:969) -- genau die
 * Fehlerklasse, die Migration 0062 gerade beseitigt hat. Und den Artikelwert in die
 * Kategorie-Zeile zu schreiben, wuerde ihn jedem anderen Artikel der Kategorie
 * ueberstuelpen; bei `title` verbietet die Feldtabelle das ausdruecklich. Die Zuordnung
 * ist hier also eine Aussage ueber den Ist-Zustand, kein zusaetzlicher Datensatz.
 */
export function quellenAuswahl(m: ExistingFieldMapping | undefined, override: unknown): string {
  if (m) return sourceSelectValue(m);
  return hatArtikelwert(override) ? '__fixed_value__' : '__unmapped__';
}

/**
 * Das Mapping, das die Wert-Spalte rendern soll. Ohne Kategorie-Zeile, aber mit
 * Artikelwert wird ein LEERER Festwert vorgetaeuscht -- damit die Kategorie-Eingabe
 * sichtbar und benutzbar bleibt, falls der Haendler den Wert doch fuer die ganze
 * Kategorie setzen will. Gespeichert wird sie nur mit Inhalt (Riegel in
 * views/tabs/kaufland-list-mapping.ts).
 */
export function anzeigeMapping(
  m: ExistingFieldMapping | undefined,
  override: unknown,
): ExistingFieldMapping | undefined {
  if (m) return m;
  if (!hatArtikelwert(override)) return undefined;
  return {
    kaufland_field_key: '',
    field_group: '',
    source_type: 'fixed_value',
    vario_path: null,
    fixed_value: null,
    template: null,
    fallback: null,
  } as ExistingFieldMapping;
}

export function renderValueInput(
  f: SchemaField,
  m: ExistingFieldMapping | undefined,
): string {
  // Festwert und Template gelten fuer die ganze Kategorie (das Mapping-Profil), nicht nur fuer
  // ein Listing — anders als der Artikel-Override direkt darunter. Ohne diese Unterschrift sieht
  // die Spalte wie eine Artikel-Eingabe aus, obwohl ein hier eingetippter Festwert ALLE Artikel
  // der Kategorie aendert. Bis 2026-08-06 hing sie an einem Schalter, den nur der inzwischen
  // entfernte Reiter "Optionale Felder" setzte — im Editor gilt derselbe Irrtum, also steht sie
  // jetzt immer. Nur bei einer echten Werteingabe sinnvoll, deshalb hier lokal statt am Ende der
  // Funktion (unmapped/vario_path liefern leeren Inhalt).
  const scopeCaption = `<div class="kl-map-field-value-cap">gilt für diese Kategorie</div>`;

  if (!m || m.source_type === 'unmapped' || m.source_type === 'clear') {
    // Kein „—"-Platzhalter mehr — Spalte 2 zeigt bereits „nicht gemappt", Spalte 3 bleibt
    // allein der Override-Eingabe vorbehalten. Bei `clear` gibt es ebenfalls nichts
    // einzutragen: Der Auftrag lautet „entfernen", nicht „setze diesen Wert".
    return '';
  }
  if (m.source_type === 'fixed_value') {
    const raw = m.fixed_value;
    const str = raw === null || raw === undefined ? '' : typeof raw === 'string' ? raw : JSON.stringify(raw);

    if (f.allowedValues && f.allowedValues.length > 0) {
      // Fix-Runde 1, Befund 1: Nicht jede Werteliste darf Handeingabe erlauben. Geschlossene
      // Enums (condition, vat_indicator, id_shipping_group -- allowedValuesSource === 'enum')
      // sind vollstaendig; ein getippter Wert ausserhalb ist immer ein Fehler und wuerde
      // ungeprueft bis zu Kaufland durchlaufen (validatePayloads/createUnit pruefen nur auf
      // "nicht leer", nicht auf Gueltigkeit). Dort bleibt es deshalb beim strikten `<select>`.
      // Kauflands eigene Shared-Set-Listen (allowedValuesSource === 'shared_set') sind
      // moeglicherweise unvollstaendig oder gekuerzt (allowedValuesTruncated) -- dort ist
      // Handeingabe der einzige Weg zu einem fehlenden Wert, deshalb die Combobox. Fehlt die
      // Herkunftsangabe (aeltere/unbekannte Aufrufer), gilt der strengere Pfad als Default.
      const erlaubtHandeingabe = f.allowedValuesSource === 'shared_set'
        || f.sharedSetPending === true
        || f.allowedValuesTruncated === true;

      if (erlaubtHandeingabe) {
        // Kauflands Werteliste kann laenger als das Auslieferungslimit (500 Werte) sein. Dann
        // ist die Combobox unvollstaendig, und die Handeingabe ist der einzige Weg zu einem
        // Wert, der in der gekuerzten Liste fehlt (z.B. eine seltene Marke).
        const gekuerztHinweis = f.allowedValuesTruncated
          ? `<div class="kl-map-field-value-hint">Liste gekürzt, Kaufland liefert mehr als 500 Werte. Fehlt der gesuchte Wert, kann er von Hand eingetragen werden.</div>`
          : '';
        return `
          ${renderCombobox({
            fieldKey: f.key,
            werte: f.allowedValues.map((av) => ({ value: av.value, label: av.label ?? av.value })),
            aktuell: str,
          })}
          ${gekuerztHinweis}
          ${scopeCaption}
        `;
      }

      const opts_ = f.allowedValues.map((av) => {
        const sel = av.value === str ? ' selected' : '';
        return `<option value="${escapeHtml(av.value)}"${sel}>${escapeHtml(av.label ?? av.value)}</option>`;
      }).join('');
      return `
        <select data-field-value="${escapeHtml(f.key)}">
          <option value="">— bitte wählen —</option>
          ${opts_}
        </select>
        <div class="kl-map-field-value-hint">Direkter Wert aus der vorgegebenen Liste.</div>
        ${scopeCaption}
      `;
    }
    if (f.sharedSetPending === true) {
      // Der Cache ist noch leer (Refresh-Job laeuft oder ist gescheitert), es gibt also noch
      // keine allowedValues zum Anzeigen -- ein Textfeld mit Ladehinweis haelt den Haendler
      // trotzdem arbeitsfaehig statt ihn vor einer leeren Auswahl zu blockieren.
      return `
        <input type="text" data-field-value="${escapeHtml(f.key)}" value="${escapeHtml(str)}"
               placeholder="Fester Wert …" />
        <div class="kl-map-field-desc">Auswahlwerte werden geladen …</div>
        ${scopeCaption}
      `;
    }

    // Feldgröße nach Kaufland-Feldtyp. Ein einzeiliges Input für eine Langbeschreibung ist
    // unbenutzbar — man sieht 40 von 65535 Zeichen. Zahlen bekommen umgekehrt ein enges
    // Number-Feld, damit Tippfehler auffallen.
    const groesse = fixedValueLayout(f);
    if (groesse.kind === 'longtext') {
      const zeichen = str.length;
      const limit = f.maxLength ?? null;
      // Kaufland dokumentiert die HTML-Regel nur fuer dataType 'text' (Langbeschreibung).
      // Fuer alle uebrigen Langtexte (z.B. small_text oder ein Kategorie-Attribut mit grosser
      // maxLength) ist unklar, ob HTML erlaubt ist -- eine geratene Zusage waere schlechter als
      // Schweigen, weil sie wie eine Auskunft aussieht. Der Vorschau-Knopf bleibt trotzdem: er
      // zeigt nur an, was eingetippt wurde, und verspricht nichts ueber Kauflands Annahme.
      const htmlHinweis = f.dataType === 'text'
        ? ' Kaufland nimmt in Beschreibungen HTML an — die Vorschau zeigt, wie es gerendert aussieht.'
        : '';
      return `
        <textarea data-field-value="${escapeHtml(f.key)}" rows="${groesse.rows}" class="kl-map-longtext" placeholder="Fester Wert …">${escapeHtml(str)}</textarea>
        <div class="kl-map-longtext-bar">
          <span class="kl-map-charcount" data-charcount-for="${escapeHtml(f.key)}">${zeichen}${limit ? ` / ${limit}` : ''} Zeichen</span>
          <button type="button" class="kl-map-html-toggle" data-html-preview-for="${escapeHtml(f.key)}">HTML-Vorschau</button>
        </div>
        <iframe class="kl-map-html-preview" data-html-preview-of="${escapeHtml(f.key)}" sandbox referrerpolicy="no-referrer" title="HTML-Vorschau" hidden></iframe>
        <div class="kl-map-field-value-hint">Hardcode-Wert für jeden Push.${htmlHinweis}</div>
        ${scopeCaption}
      `;
    }
    if (groesse.kind === 'number') {
      return `
        <input type="number" step="any" data-field-value="${escapeHtml(f.key)}" value="${escapeHtml(str)}" placeholder="Fester Wert …" />
        <div class="kl-map-field-value-hint">Hardcode-Wert für jeden Push.</div>
        ${scopeCaption}
      `;
    }
    // Suchbegriffe (dataType 'keywords'): eigener Hinweis statt des generischen. Kaufland nimmt
    // hier ausdruecklich KEIN HTML an, das Feld ist rein intern fuer die Suche und erscheint nie
    // auf der Produktseite -- die Kurzbeschreibung ist trotz ihres Namens keine Beschreibung.
    const wertHinweis = f.dataType === 'keywords'
      ? 'Kommagetrennte Suchbegriffe, hoechstens zehn. Kein HTML, erscheint nicht auf der Produktseite.'
      : 'Hardcode-Wert für jeden Push.';
    return `
      <input type="text" data-field-value="${escapeHtml(f.key)}" value="${escapeHtml(str)}"${f.maxLength ? ` maxlength="${f.maxLength}"` : ''} placeholder="Fester Wert …" />
      <div class="kl-map-field-value-hint">${wertHinweis}</div>
      ${scopeCaption}
    `;
  }
  if (m.source_type === 'template') {
    return `
      <textarea data-field-value="${escapeHtml(f.key)}" rows="2" placeholder="z.B. {listing.name} ({article.manufacturerRefLabel})">${escapeHtml(m.template ?? '')}</textarea>
      <div class="kl-map-field-value-hint">Platzhalter <code>{sourceKey}</code> → Preview resolved gegen Beispielartikel.</div>
      ${scopeCaption}
    `;
  }
  if (m.source_type === 'vario_dms_resource') {
    return `
      <div class="kl-map-field-value-vario">
        <span class="v-icon fa-light fa-image"></span>
        <code>DMS: ${escapeHtml(m.vario_resource_id ?? '')}</code>
      </div>
      <div class="kl-map-field-value-hint">Bild aus dem Vario-DMS - Public-URL wird beim Push automatisch eingesetzt.</div>
    `;
  }
  // vario_path: kein blauer Quell-Pfad-Chip mehr (z.B. listing.ean/listing.name) — die
  // gewaehlte Quelle steht bereits im Mapping-Dropdown (Spalte 2). Spalte 3 ("manuelle
  // Eingabe") bleibt allein dem Listing-Override vorbehalten.
  return '';
}

export function renderAllowedValues(f: SchemaField): string {
  if (!f.allowedValues || f.allowedValues.length === 0) return '';
  const labels = f.allowedValues.slice(0, 3).map((v) => v.label ?? v.value).join(' · ');
  const extra = f.allowedValues.length > 3 ? ` · +${f.allowedValues.length - 3} weitere` : '';
  return `<div class="kl-map-field-allowed">Erlaubte Werte: ${escapeHtml(labels)}${extra}</div>`;
}

/**
 * Rendert die HTML-Vorschau eines Langtext-Direktwerts in ein abgeschottetes iframe.
 *
 * **Warum iframe und nicht `innerHTML` mit Allowlist:** Der Text stammt aus einem Eingabefeld und
 * würde sonst im DOM derselben Seite landen, die die Vario-Session hält — ein `<script>` oder ein
 * `onerror=`-Attribut wäre ausführbarer Code im App-Kontext. Ein handgeschriebener Sanitizer wäre
 * hier der schwächere Schutz: Er müsste alle Umgehungen kennen, und in dieser Toolchain gibt es
 * keine DOM-Testumgebung (`vitest` läuft auf `node`), mit der man ihn belegen könnte.
 *
 * `sandbox` ohne jedes Token verbietet Skripte, Formulare, Navigation und same-origin-Zugriff.
 * Der Schutz hängt damit am Browser, nicht an meiner Tag-Liste. `srcdoc` statt `src`, damit kein
 * Netzwerkzugriff nötig ist.
 *
 * Hier statt in einem Aufrufer, weil das Markup, das dieses iframe erzeugt
 * (`.kl-map-html-preview[data-html-preview-of]`), aus `renderValueInput` in dieser Datei kommt.
 * Wer das iframe-Markup rendert, liefert auch die Funktion, die es fuellt.
 */
export function renderHtmlPreview(host: HTMLIFrameElement, roh: string): void {
  const leer = roh.trim().length === 0;
  const inhalt = leer ? '<em style="color:#98A2B3">(leer)</em>' : roh;
  host.srcdoc = `<!doctype html><meta charset="utf-8">`
    + `<style>body{margin:0;padding:8px;font:13px/1.5 system-ui,sans-serif;color:#1A1D21;}`
    + `img{max-width:100%;height:auto}table{border-collapse:collapse}td,th{border:1px solid #ddd;padding:4px}</style>`
    + inhalt;
}

// ---------- Row Styles ----------

/**
 * CSS fuer das kl-map-field-Markup aus `renderFieldRow`/`renderValueInput`/`renderOverrideInput`.
 *
 * Steht hier und nicht beim Editor, weil das Markup hier entsteht: wer die Zeilen rendert,
 * liefert auch ihre CSS. Genau daran hing der Bug, als der inzwischen entfernte Reiter
 * "Optionale Felder" dasselbe kl-map-field-Markup rendete, aber ohne dessen Regeln — Label,
 * Typeline, Status-Streifen und die `is-set`-Hervorhebung der Override-Spalte fehlten dort
 * ersatzlos. Kopfbereich, Gruppen, Progressbar, Quickbar und Sticky-Actions gehoeren zum Layout
 * des Editors und bleiben dort in COMPONENT_STYLES.
 */
export const FIELD_ROW_STYLES = `
  .kl-map-field {
    display: grid;
    /* Drei gleichwertige Spalten: Feldanzeige · Mapping-Dropdown · manuelle Eingabe. */
    grid-template-columns: minmax(200px, 1fr) minmax(180px, 1fr) minmax(180px, 1fr);
    gap: 14px;
    padding: 12px 14px 12px 16px;
    position: relative;
    align-items: start;
    transition: background 160ms ease-out;
  }
  .kl-map-field-source { align-self: start; }
  .kl-map-field[hidden] { display: none; }
  .kl-map-field::before {
    content: '';
    position: absolute;
    left: 0; top: 0; bottom: 0;
    width: 3px;
    background: #E4E7EC;
  }
  .kl-map-field[data-status="mapped"]::before  { background: #15803D; }
  .kl-map-field[data-status="required-empty"]::before { background: var(--go-orange, #1A1D21); }
  .kl-map-field[data-status="fixed"]::before    { background: #15803D; }
  .kl-map-field[data-status="template"]::before { background: #15803D; }
  /* Loeschauftrag: rot, weil dieser Push bei Kaufland etwas ENTFERNT. Bewusst nicht gruen
     wie ein gesetzter Wert und nicht grau wie „nicht gemappt" — beides waere harmloser,
     als es ist. */
  .kl-map-field[data-status="clear"]::before    { background: #B42318; }

  /* Hellgelb fuer nicht-gemappte Pflichtfelder, sehr leicht fuer optionale */
  .kl-map-field[data-status="required-empty"] { background: #FFFBEB; }
  .kl-map-field[data-status="empty"]:not([data-status="required-empty"]) { background: #FAFAFA; }

  .kl-map-field-meta { display: flex; flex-direction: column; gap: 3px; min-width: 0; }
  .kl-map-field-label-row { display: flex; align-items: baseline; gap: 6px; }
  .kl-map-field-label {
    font-weight: 600; font-size: 14px; color: #101828;
    cursor: help;
  }
  .kl-map-field-req {
    color: var(--go-orange, #1A1D21);
    font-weight: 700;
    font-size: 14px;
    line-height: 1;
  }
  .kl-map-field-info {
    color: #98A2B3;
    font-size: 12px;
    cursor: help;
  }
  .kl-map-field-typeline {
    font-family: 'JetBrains Mono', ui-monospace, monospace;
    font-size: 11px;
    color: #667085;
    text-transform: lowercase;
    letter-spacing: 0.02em;
  }
  .kl-map-field-desc {
    font-size: 11.5px;
    color: #475467;
    line-height: 1.35;
    margin-top: 2px;
  }
  .kl-map-field-conditional {
    font-size: 11px;
    color: #B58A14;
    font-style: italic;
    margin-top: 2px;
  }
  .kl-map-field-allowed {
    font-size: 11px;
    color: #98A2B3;
    margin-top: 2px;
  }

  /* Pfeil zw. Source und Target */
  .kl-map-field-arrow {
    color: #98A2B3;
    font-size: 14px;
    text-align: center;
  }

  /* Mapping-Source-Dropdown */
  .kl-map-field-source select {
    width: 100%;
    height: 36px;
    padding: 0 12px;
    border: 1px solid #D0D5DD;
    border-radius: 6px;
    background: #fff;
    font: inherit;
    font-size: 13px;
    color: #101828;
    cursor: pointer;
    transition: border-color 140ms ease, box-shadow 140ms ease;
  }
  .kl-map-field-source select:hover { border-color: #98A2B3; }
  .kl-map-field-source select:focus {
    outline: 0;
    border-color: var(--go-orange, #1A1D21);
    box-shadow: 0 0 0 3px rgba(26,29,33,0.18);
  }

  /* Value-Input (rechte Spalte) */
  .kl-map-field-value-shell { display: flex; flex-direction: column; gap: 3px; min-width: 0; }
  .kl-map-field-value-shell input,
  .kl-map-field-value-shell textarea,
  .kl-map-field-value-shell select {
    width: 100%;
    height: 36px;
    padding: 0 12px;
    border: 1px solid #D0D5DD;
    border-radius: 6px;
    background: #fff;
    font: inherit;
    font-size: 13px;
    color: #101828;
  }
  .kl-map-field-value-shell textarea {
    font-family: 'JetBrains Mono', ui-monospace, monospace;
    font-size: 12px;
    min-height: 64px;
    height: auto;
    padding: 8px 12px;
    resize: vertical;
  }
  .kl-map-field-value-shell input:focus,
  .kl-map-field-value-shell textarea:focus,
  .kl-map-field-value-shell select:focus {
    outline: 0;
    border-color: var(--go-orange, #1A1D21);
    box-shadow: 0 0 0 3px rgba(26,29,33,0.18);
  }
  .kl-map-field-value-hint { font-size: 11px; color: #98A2B3; }
  /* Unterschrift unter Festwert/Template: dieselbe Machart wie die Override-Caption darunter
     (.kl-map-field-override-cap), aber statisch — es gibt kein "gesetzt/nicht gesetzt" fuer die
     Kategorie-Zuordnung, deshalb kein .is-set-Gegenstueck. */
  .kl-map-field-value-cap {
    font-size: 10px; color: #98A2B3; text-transform: uppercase; letter-spacing: 0.03em;
  }
  /* Langtext-Direktwerte: Feld nach Kaufland-Feldtyp bemessen, dazu Zeichenzaehler und die
     iframe-Vorschau. Monospace, weil man hier HTML tippt und Tags zaehlen will. */
  .kl-map-longtext {
    width: 100%; box-sizing: border-box; resize: vertical;
    font-family: 'JetBrains Mono', ui-monospace, monospace;
    font-size: 12px; line-height: 1.5; padding: 6px 8px;
    border: 1px solid var(--v-border, #D0D5DD); border-radius: 6px;
  }
  .kl-map-longtext:focus {
    outline: 0; border-color: var(--go-orange, #1A1D21);
    box-shadow: 0 0 0 3px rgba(26,29,33,0.18);
  }
  .kl-map-longtext-bar {
    display: flex; align-items: center; justify-content: space-between; gap: 8px; margin-top: 3px;
  }
  .kl-map-charcount { font-size: 11px; color: #98A2B3; font-variant-numeric: tabular-nums; }
  .kl-map-charcount.is-over { color: var(--v-danger, #B42318); font-weight: 600; }
  .kl-map-html-toggle {
    font: inherit; font-size: 11px; background: none; border: 0; padding: 0;
    color: var(--vario-green-dark, #4C7A1E); cursor: pointer; text-decoration: underline;
  }
  .kl-map-html-preview {
    width: 100%; height: 200px; margin-top: 4px; box-sizing: border-box;
    border: 1px solid var(--v-border, #D0D5DD); border-radius: 6px; background: #fff;
  }
  .kl-map-html-preview[hidden] { display: none; }
  .kl-map-field-value-vario {
    font-size: 11.5px; color: #475467;
    display: flex; align-items: center; gap: 6px; padding: 4px 0;
  }
  .kl-map-field-value-vario code {
    font-family: 'JetBrains Mono', ui-monospace, monospace;
    font-size: 12px; color: #1849A9;
  }
  .kl-map-field-value-empty {
    font-size: 12px; color: #98A2B3; font-style: italic; padding: 4px 0;
  }

  /* Manuelle Eingabe / Listing-Override (Spalte 3) */
  .kl-map-field-override { display: flex; flex-direction: column; gap: 3px; }
  .kl-map-field-override-input.is-set { border-color: #F59E0B !important; }
  .kl-map-field-override-cap {
    font-size: 10px; color: #98A2B3; text-transform: uppercase; letter-spacing: 0.03em;
  }
  .kl-map-field-override-cap.is-set { color: #B45309; font-weight: 600; }

  @media (max-width: 900px) {
    .kl-map-field {
      grid-template-columns: 1fr;
      gap: 8px;
    }
    .kl-map-field-arrow { display: none; }
  }
`;

// ---------- State helpers ----------

export function isMapped(m: ExistingFieldMapping | undefined): boolean {
  if (!m) return false;
  if (m.source_type === 'unmapped') return false;
  if (m.source_type === 'fixed_value') return m.fixed_value !== null && m.fixed_value !== undefined && m.fixed_value !== '';
  if (m.source_type === 'template') return Boolean(m.template && m.template.length > 0);
  if (m.source_type === 'vario_path') return Boolean(m.vario_path);
  if (m.source_type === 'vario_dms_resource') return Boolean(m.vario_resource_id);
  return false;
}

export function fieldStatus(f: DisplayField, m: ExistingFieldMapping | undefined): string {
  if (f.required && !isMapped(m)) return 'required-empty';
  // Eigener Status statt „unmapped": Ein aktiver Loeschauftrag ist etwas anderes als ein
  // Feld, das man nie zugeordnet hat — das soll man in der Liste sehen.
  if (m?.source_type === 'clear') return 'clear';
  if (!m || m.source_type === 'unmapped') return 'unmapped';
  if (m.source_type === 'fixed_value') return 'fixed';
  if (m.source_type === 'template') return 'template';
  if (m.source_type === 'vario_dms_resource') return 'mapped';
  return 'mapped';
}

/**
 * Wandelt den Roh-Input eines Artikel-Overrides in den jsonb-tauglichen Wert um.
 *
 * Leer loescht den Override. Zahlentypen werden als Zahl gespeichert, sonst als String —
 * damit ein Vergleich mit dem Zahlenwert im Push funktioniert und nicht an "42" vs. 42
 * scheitert. Eine nicht parsbare Eingabe bei einem Zahlentyp (z.B. "abc" fuer `integer`)
 * bleibt Rohtext statt `NaN`, sonst würde der Override selbst kaputt gespeichert.
 *
 * Stand bis 2026-08-06 zweimal identisch als saveOverride-interne if/else: in
 * kaufland-list-mapping.ts UND in dem inzwischen entfernten optional-fields.ts. Die Regel gehoert
 * hierher, weil sie zum Feldtyp gehoert, den auch die Zeile rendert — das geteilte Modul rendert
 * und klassifiziert, es speichert nicht.
 */
export function parseOverrideValue(raw: string, dataType: string | undefined): unknown {
  if (raw === '') return null;
  if (dataType === 'currency_cents' || dataType === 'integer' || dataType === 'float') {
    const n = Number(raw);
    return Number.isFinite(n) ? n : raw;
  }
  return raw;
}

// ---------- Helpers ----------

export function escapeHtml(s: string | number | null | undefined): string {
  if (s === null || s === undefined) return '';
  return String(s)
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;');
}

export function cssEscape(s: string): string {
  // Minimaler escaper für attribute-selectors. Deckt unsere field-keys ab (ascii + dots).
  return s.replace(/(["\\])/g, '\\$1');
}
